import * as i0 from '@angular/core';
import { Injectable } from '@angular/core';
import { throwError, fromEvent } from 'rxjs';
import { filter, map } from 'rxjs/operators';

/** Adaption from https://github.com/Adyen/adyen-web/pull/2028/files - storage.ts */
class NonPersistentStorage {
  constructor() {
    this.storage = {};
  }
  get length() {
    return Object.keys(this.storage).length;
  }
  /** Returns the name of the nth key, or null if n is greater than or equal to the number of key/value pairs. */
  key(index) {
    if (index > this.length) {
      return null;
    }
    return Object.keys(this.storage)[index] || null;
  }
  /** Returns the current value associated with the given key, or null if the given key does not exist. */
  getItem(keyName) {
    return this.storage[keyName] || null;
  }
  /** Sets the value of the pair identified by key to value, creating a new key/value pair if none existed for key previously. */
  setItem(keyName, keyValue) {
    this.storage[keyName] = keyValue;
  }
  /** Removes the key/value pair with the given key, if a key/value pair with the given key exists. */
  removeItem(keyName) {
    delete this.storage[keyName];
  }
  /** Removes all key/value pairs, if there are any. */
  clear() {
    this.storage = {};
  }
}
let StorageService = /*#__PURE__*/(() => {
  class StorageService {
    constructor() {
      this.localStorage = this.isStorageAvailable('localStorage') ? window.localStorage : new NonPersistentStorage();
      this.sessionStorage = this.isStorageAvailable('sessionStorage') ? window.sessionStorage : new NonPersistentStorage();
    }
    /**
     * Retrieves a specific item from the browser's local storage.
     * @param key The item to be retrieved.
     * @returns The specified item in local storage if it exists, otherwise null.
     */
    getItem(key, isLocal = true) {
      let item = this.getStorage(isLocal).getItem(key);
      if (item === 'undefined' || item === 'null') {
        item = null;
      } else {
        item = this.tryParseJSONObject(item);
      }
      return item;
    }
    /**
     * Tries to parse the Json string and cast to an expected object.
     * @param jsonString string to try converting to Json/
     * @returns either an expected object (T), or original string.
     */
    tryParseJSONObject(jsonString) {
      try {
        const parsedString = JSON.parse(jsonString);
        if (!!parsedString && typeof parsedString === 'object') {
          return parsedString;
        }
      } catch (e) {
        // do nothing
      }
      return jsonString;
    }
    /**
     * Updates or creates the given item in local storage.
     * @param key The key of the item to create or update.
     * @param value The value of the item to create or update as string.
     */
    setItem(key, value, isLocal = true) {
      this.getStorage(isLocal).setItem(key, value);
    }
    /**
     * Removes the given item from local storage.
     * @param key The key of the item to be removed.
     */
    removeItem(key, isLocal = true) {
      this.getStorage(isLocal).removeItem(key);
    }
    /**
     * Clears all the local storage items. Useful for logout events.
     */
    clearStorage(isLocal = true) {
      this.getStorage(isLocal).clear();
    }
    /**
     * Observes a given item in local storage and updates observer
     * when the item is updated in another browser tab.
     * @param key The key of the item to be observed.
     * @returns An observable that emits the given item's new value
     * whenever it is updated.
     */
    observeStorageEventItem(key, isLocal = true) {
      return this.windowStorageEvent(isLocal).pipe(filter(event => event.storageArea === this.getStorage(isLocal)), filter(event => event.key === key), map(event => event.newValue));
    }
    /**
     * Observes an array of given items in local storage and updates observer
     * when the item is updated in another browser tab.
     * @param keys The array of keys to be observed.
     * @returns An observable that emits the given item's new key and value
     * whenever it is updated.
     */
    observeStorageEventArray(keys, isLocal = true) {
      return this.windowStorageEvent(isLocal).pipe(filter(event => event.storageArea === this.getStorage(isLocal)), filter(event => !!event.key && keys.includes(event.key)), map(event => ({
        key: event.key,
        value: event.newValue
      })));
    }
    /**
     * Checks if local or session storage is available.
     * Note Safari (in private mode) has a functional localStorage object (with a quota of zero).
     * Therefore we need to actively set an item to check that it is real and functional.
     * Modified from https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#feature-detecting_localstorage
     */
    isStorageAvailable(type) {
      try {
        const storage = window[type];
        const storeText = '__storage_test__';
        storage.setItem(storeText, storeText);
        storage.removeItem(storeText);
        return true;
      } catch (_) {
        return false;
      }
    }
    windowStorageEvent(isLocal) {
      if (!this.isStorageAvailable(isLocal ? 'localStorage' : 'sessionStorage')) {
        return throwError(() => 'No windows storage events available to observe');
      }
      return fromEvent(window, 'storage');
    }
    /**
     * Fetches the browser's full local storage.
     * @returns A storage object containing methods to access and update the
     * browser's local storage.
     */
    getStorage(isLocal) {
      return isLocal ? this.localStorage : this.sessionStorage;
    }
  }
  StorageService.ɵfac = function StorageService_Factory(t) {
    return new (t || StorageService)();
  };
  StorageService.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: StorageService,
    factory: StorageService.ɵfac,
    providedIn: 'root'
  });
  return StorageService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NonPersistentStorage, StorageService };
